/**
 * Device Cache Wrapper
 * Migrates to and uses encrypted cache for all device data
 * Maintains backward compatibility while ensuring all new data is encrypted
 */

import { DeviceInfo } from '../common/enums'
import { encryptedDeviceCache } from './encryptedDeviceCache'

const OLD_DB_NAME = 'ioag-device-cache'
const OLD_DB_VERSION = 1
const OLD_STORE_NAME = 'devices'

interface OldCachedDevices {
  email: string
  devices: DeviceInfo[]
  timestamp: number
}

/**
 * Device Cache that automatically uses encryption
 * Provides migration from old unencrypted cache
 */
class DeviceCache {
  private migrationInProgress = new Map<string, Promise<void>>()

  /**
   * Migrate data from old unencrypted cache if it exists
   */
  private async migrateFromOldCache(email: string): Promise<void> {
    // Check if migration is already in progress for this user
    if (this.migrationInProgress.has(email)) {
      return this.migrationInProgress.get(email)!
    }

    const migrationPromise = this.performMigration(email)
    this.migrationInProgress.set(email, migrationPromise)

    try {
      await migrationPromise
    } finally {
      this.migrationInProgress.delete(email)
    }
  }

  private async performMigration(email: string): Promise<void> {
    try {
      // Try to open the old database
      const oldDb = await new Promise<IDBDatabase | null>((resolve) => {
        const request = indexedDB.open(OLD_DB_NAME, OLD_DB_VERSION)

        request.onerror = () => resolve(null)
        request.onsuccess = () => resolve(request.result)
        request.onupgradeneeded = () => resolve(null) // Old DB doesn't exist
      })

      if (!oldDb) return

      // Check if the store exists
      if (!oldDb.objectStoreNames.contains(OLD_STORE_NAME)) {
        oldDb.close()
        return
      }

      // Read old data
      const oldData = await new Promise<OldCachedDevices | null>((resolve) => {
        try {
          const transaction = oldDb.transaction([OLD_STORE_NAME], 'readonly')
          const store = transaction.objectStore(OLD_STORE_NAME)
          const request = store.get(email)

          request.onsuccess = () => resolve(request.result || null)
          request.onerror = () => resolve(null)
        } catch {
          resolve(null)
        }
      })

      oldDb.close()

      if (oldData && oldData.devices && oldData.devices.length > 0) {
        console.log(`🔄 Migrating ${oldData.devices.length} devices to encrypted storage`)

        // Migrate to encrypted cache
        await encryptedDeviceCache.setDevices(email, oldData.devices)

        // Delete old data after successful migration
        await this.deleteOldCacheEntry(email)

        console.log(`✅ Successfully migrated devices to encrypted storage`)
      }
    } catch (error) {
      console.error('Migration error (non-critical):', error)
      // Migration failures are non-critical, we'll just use the new cache
    }
  }

  private async deleteOldCacheEntry(email: string): Promise<void> {
    try {
      const oldDb = await new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(OLD_DB_NAME, OLD_DB_VERSION)
        request.onerror = () => reject(request.error)
        request.onsuccess = () => resolve(request.result)
      })

      await new Promise<void>((resolve, reject) => {
        const transaction = oldDb.transaction([OLD_STORE_NAME], 'readwrite')
        const store = transaction.objectStore(OLD_STORE_NAME)
        const request = store.delete(email)

        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })

      oldDb.close()
      console.log('🗑️ Removed old unencrypted cache entry')
    } catch (error) {
      console.error('Error deleting old cache entry:', error)
    }
  }

  /**
   * Get cached devices (now always encrypted)
   */
  async getDevices(email: string): Promise<DeviceInfo[] | null> {
    // First, try to migrate old data if it exists
    await this.migrateFromOldCache(email)

    // Then use the encrypted cache
    return encryptedDeviceCache.getDevices(email)
  }

  /**
   * Cache devices (now always encrypted)
   */
  async setDevices(email: string, devices: DeviceInfo[]): Promise<void> {
    return encryptedDeviceCache.setDevices(email, devices)
  }

  /**
   * Clear cached devices
   */
  async clearDevices(email: string): Promise<void> {
    return encryptedDeviceCache.clearDevices(email)
  }

  /**
   * Clear all cached devices
   */
  async clearAll(): Promise<void> {
    // Clear encrypted cache
    await encryptedDeviceCache.clearAll()

    // Also try to clear old cache if it exists
    try {
      await indexedDB.deleteDatabase(OLD_DB_NAME)
      console.log('🗑️ Removed old unencrypted database')
    } catch (error) {
      console.error('Error removing old database:', error)
    }
  }

  /**
   * Get cache metadata
   */
  async getCacheMetadata(email: string): Promise<{ timestamp: number } | null> {
    return encryptedDeviceCache.getCacheMetadata(email)
  }

  /**
   * Clean up old unencrypted database completely
   */
  async cleanupOldDatabase(): Promise<void> {
    try {
      await indexedDB.deleteDatabase(OLD_DB_NAME)
      console.log('🧹 Cleaned up old unencrypted database')
    } catch (error) {
      console.error('Error cleaning up old database:', error)
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    return encryptedDeviceCache.getCacheStats()
  }

  /**
   * Get user-specific cache statistics
   */
  async getUserCacheStats(email: string) {
    return encryptedDeviceCache.getUserCacheStats(email)
  }
}

// Export singleton instance
export const deviceCache = new DeviceCache()